import { useState } from 'react';
import { parseISO, format } from 'date-fns';
import { Camera, MessageSquarePlus, Clock, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { PAPER, typeMeta } from './theme';

/**
 * Cost Ledger L-2 §0.3 — capture entry card in the diary timeline.
 *
 * The companion diary entry is the day-record reflection of a financial
 * ledger row. It renders:
 *   - photo thumbnail (tap opens the stitched PDF on OneDrive)
 *   - badges: category, supplier, reference, tags
 *   - value: admin + surveyor only. NEVER rendered for operatives —
 *     the API response has no `value_pence` key for them.
 *   - value_pending indicator when the ledger row is still waiting
 *     for a price (admin + surveyor only).
 *   - notes thread, oldest first, immutable
 *   - "+ Add note" — the only permitted modification
 *
 * Edit and Delete buttons are INTENTIONALLY absent — a capture entry
 * cannot be altered from the diary. Corrections happen on the ledger
 * row from /cost-ledger by an admin, with their own audit trail.
 */

function fmtGbpFromPence(pence) {
  const n = Number(pence) || 0;
  return `£${(n / 100).toFixed(2)}`;
}

export function CaptureEntryCard({ entry, onNoteAdded }) {
  const { api, isAdmin, isSurveyor } = useAuth();
  const canSeeValue = isAdmin || isSurveyor;
  const [expanded, setExpanded] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [posting, setPosting] = useState(false);

  const meta = typeMeta('capture');
  const Icon = meta.icon || Camera;
  const created = entry.created_at ? parseISO(entry.created_at) : null;
  const timeLabel = created ? format(created, 'HH:mm') : '';

  // Hydrated payload from the backend list-entries call.
  const ledger = entry.payload?.ledger || {};
  const photoUrl = entry.payload?.photo_web_url;
  const category = ledger.category;
  const supplier = ledger.supplier_name;
  const reference = ledger.reference || entry.reference;
  const tags = ledger.tags || [];
  const valuePending = !!ledger.value_pending;
  const voided = !!ledger.voided;
  // value_pence is genuinely absent from `ledger` for operatives —
  // we don't just skip the render, the data isn't in the response.
  const valueLabel = canSeeValue
    ? (valuePending ? 'Value pending' : (typeof ledger.value_pence === 'number' ? fmtGbpFromPence(ledger.value_pence) : null))
    : null;

  const notes = Array.isArray(entry.notes) ? entry.notes : [];

  const submitNote = async () => {
    const text = noteText.trim();
    if (!text) return;
    setPosting(true);
    try {
      await api.post(`/diary/entries/${entry.id}/notes`, { text });
      setNoteText('');
      toast.success('Note added');
      onNoteAdded?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add note');
    } finally {
      setPosting(false);
    }
  };

  const CATEGORY_COLOURS = {
    material: { bg: '#DBEAFE', fg: '#1E40AF' },
    plant:    { bg: '#F3E8FF', fg: '#6B21A8' },
    other:    { bg: '#F4F4F5', fg: '#3F3F46' },
  };
  const catStyle = category ? CATEGORY_COLOURS[category] : null;

  return (
    <div
      className="rounded-xl px-3.5 py-3 flex gap-3 transition-all"
      style={{
        background: PAPER.card,
        border: `1px solid ${expanded ? PAPER.accent : PAPER.border}`,
        boxShadow: PAPER.shadow,
        opacity: voided ? 0.6 : 1,
      }}
      data-testid={`timeline-entry-${entry.id}`}
    >
      {/* Icon or photo thumb tile */}
      {photoUrl ? (
        <a
          href={photoUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="w-14 h-14 rounded-lg flex-shrink-0 overflow-hidden block"
          style={{ border: `1px solid ${PAPER.border}` }}
          onClick={(e) => e.stopPropagation()}
          data-testid={`capture-entry-photo-${entry.id}`}
        >
          <img
            src={photoUrl}
            alt="Capture"
            className="w-full h-full object-cover"
            onError={(e) => {
              // If OneDrive link isn't image-embeddable, fall back to icon
              e.currentTarget.style.display = 'none';
              e.currentTarget.parentElement.innerHTML = '<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:' + meta.bg + ';color:' + meta.fg + ';"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg></div>';
            }}
          />
        </a>
      ) : (
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: meta.bg, color: meta.fg }}
        >
          <Icon className="w-5 h-5" strokeWidth={2.25} />
        </div>
      )}

      <div className="min-w-0 flex-1">
        {/* Top row — Capture label + reference + time */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: meta.fg }}>
            Capture
          </span>
          {reference && (
            <span
              className="text-[10px] px-1.5 py-0.5 rounded font-mono font-bold"
              style={{ background: PAPER.paperMuted, color: PAPER.inkSoft }}
              data-testid={`capture-entry-reference-${entry.id}`}
            >{reference}</span>
          )}
          {voided && (
            <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded" style={{ background: '#FEE2E2', color: '#B91C1C' }}>
              Voided
            </span>
          )}
          <span className="ml-auto text-[11px] flex items-center gap-1" style={{ color: PAPER.muted }}>
            <Clock className="w-3 h-3" />{timeLabel}
          </span>
        </div>

        {/* Second row — supplier name (bold) or fallback title */}
        <div className="text-[15px] font-semibold mt-0.5" style={{ color: PAPER.ink }} data-testid={`capture-entry-supplier-${entry.id}`}>
          {supplier || entry.title}
        </div>

        {/* Third row — badge chips for category / value / tags */}
        <div className="flex items-center gap-1.5 flex-wrap mt-1">
          {category && catStyle && (
            <span
              className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded"
              style={{ background: catStyle.bg, color: catStyle.fg }}
              data-testid={`capture-entry-category-${entry.id}`}
            >
              {category}
            </span>
          )}
          {valueLabel && (
            <span
              className="text-[11px] font-bold tabular-nums px-1.5 py-0.5 rounded"
              style={{
                background: valuePending ? '#FEF3C7' : PAPER.paperMuted,
                color: valuePending ? '#92400E' : PAPER.ink,
              }}
              data-testid={`capture-entry-value-${entry.id}`}
            >
              {valueLabel}
            </span>
          )}
          {tags.slice(0, 6).map(t => (
            <span
              key={t}
              className="text-[10px] px-1.5 py-0.5 rounded"
              style={{ background: PAPER.paperMuted, color: PAPER.inkSoft }}
              data-testid={`capture-entry-tag-${t}`}
            >
              #{t}
            </span>
          ))}
        </div>

        {/* Author line */}
        <div className="text-[11px] mt-1.5" style={{ color: PAPER.muted }}>
          {entry.author_name || 'Someone'}
        </div>

        {/* Expand toggle → notes thread + Add note */}
        <button
          type="button"
          onClick={() => setExpanded(x => !x)}
          className="text-[12px] mt-2 flex items-center gap-1 font-medium hover:underline"
          style={{ color: PAPER.accent }}
          data-testid={`capture-entry-toggle-${entry.id}`}
        >
          <MessageSquarePlus className="w-3.5 h-3.5" />
          {expanded ? 'Hide notes' : (notes.length ? `Notes (${notes.length})` : 'Add a note')}
        </button>

        {expanded && (
          <div className="mt-2 pt-3 border-t space-y-2.5" style={{ borderColor: PAPER.divider }} data-testid={`capture-entry-notes-${entry.id}`}>
            {notes.length === 0 && (
              <div className="text-[12px] italic" style={{ color: PAPER.muted }}>
                No notes yet. Use notes to flag a problem with this entry — they're append-only and permanent.
              </div>
            )}
            {notes.map(n => {
              const nDate = n.created_at ? parseISO(n.created_at) : null;
              return (
                <div key={n.id} className="text-[12px] rounded-md px-2.5 py-1.5" style={{ background: PAPER.paperMuted }} data-testid={`capture-entry-note-${n.id}`}>
                  <div className="text-[13px] whitespace-pre-wrap" style={{ color: PAPER.ink }}>{n.text}</div>
                  <div className="text-[10px] mt-0.5" style={{ color: PAPER.muted }}>
                    {n.created_by_name || 'Someone'}
                    {nDate && ' · ' + format(nDate, 'dd MMM yyyy HH:mm')}
                  </div>
                </div>
              );
            })}

            <div className="flex items-start gap-2">
              <textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Flag a problem with this entry…"
                rows={2}
                maxLength={2000}
                className="flex-1 text-[12px] rounded-md px-2 py-1.5 outline-none resize-none"
                style={{ background: PAPER.page, border: `1px solid ${PAPER.border}`, color: PAPER.ink }}
                data-testid={`capture-entry-note-input-${entry.id}`}
              />
              <button
                type="button"
                onClick={submitNote}
                disabled={posting || !noteText.trim()}
                className="text-[12px] font-semibold px-2.5 py-1.5 rounded-md disabled:opacity-40"
                style={{ background: PAPER.navy, color: PAPER.accent }}
                data-testid={`capture-entry-note-submit-${entry.id}`}
              >
                {posting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Post'}
              </button>
            </div>
            <div className="flex items-start gap-1.5 text-[10px]" style={{ color: PAPER.muted }}>
              <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
              <span>Notes are permanent. Nobody, including admin, can edit or delete them.</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CaptureEntryCard;
